import type { RecordingId } from "@repo/app-types/entities/recording";
import type { TeammateAccountCallOutline } from "@repo/app-types/entities/teammateAccountCallOutline";
import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { popoverStyle } from "@repo/ui/popover/style";
import type { FC } from "react";
import {
  Dialog,
  DialogTrigger,
  OverlayTriggerStateContext,
  Popover,
} from "react-aria-components";
import {
  buildCallNavigationItems,
  checkCallSegmentContainsPosition,
  type CallPosition,
} from "./callNavigationHelpers.js";
import { CallNavigationRow, CallNavigator } from "./callNavigator.js";
import { useCallAudioPosition } from "./useCallAudioPosition.js";
import { useCallNavigationActions } from "./useCallNavigationActions.js";

export interface CallNavigatorDropdownProps {
  recordings: readonly AccountGradingRecording[];
  callOutline: TeammateAccountCallOutline | undefined;
  isLoading?: boolean;
  isError?: boolean;
  callPosition: CallPosition | undefined;
  /** Includes the current key moment and reserves its line when no moment matches. */
  showCurrentKeyMoment?: boolean;
  onCallPositionRequest: (position: CallPosition) => void;
}

/** Compact call and key-moment navigation for transcript panes. */
export const CallNavigatorDropdown: FC<CallNavigatorDropdownProps> = (
  props,
) => {
  const {
    recordings,
    callOutline,
    callPosition,
    showCurrentKeyMoment = false,
  } = props;
  const calls = buildCallNavigationItems(recordings, callOutline);
  const activeCall =
    calls.find((call) => call.recording.id === callPosition?.recordingId) ??
    calls[0];

  if (!activeCall) {
    return (
      <Button variant={ButtonVariant.Secondary} isDisabled>
        No calls available
      </Button>
    );
  }

  const currentKeyMoment = showCurrentKeyMoment
    ? activeCall.segments.find((segment) =>
        checkCallSegmentContainsPosition(
          activeCall.recording.id,
          segment,
          callPosition,
        ),
      )
    : undefined;

  return (
    <DialogTrigger>
      <OverlayTriggerStateContext.Consumer>
        {(state) => (
          <CallNavigationRow
            callNumber={activeCall.callNumber}
            recordedAt={activeCall.recording.recorded_at}
            duration={activeCall.recording.duration}
            title={activeCall.title}
            keyMoment={currentKeyMoment}
            reserveKeyMomentSpace={showCurrentKeyMoment}
            isExpanded={state?.isOpen ?? false}
            className="static"
          />
        )}
      </OverlayTriggerStateContext.Consumer>
      <Popover
        placement="bottom start"
        className={popoverStyle().popover({
          className: "z-20 w-(--trigger-width)",
        })}
      >
        <Dialog aria-label="Navigate calls" className="outline-hidden">
          {({ close }) => (
            <CallNavigatorDropdownContent
              {...props}
              currentRecordingId={activeCall.recording.id}
              onClose={close}
            />
          )}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
};

interface CallNavigatorDropdownContentProps extends CallNavigatorDropdownProps {
  /** The call at the current transcript position; navigation follows it while open. */
  currentRecordingId: RecordingId;
  onClose: () => void;
}

const CallNavigatorDropdownContent: FC<CallNavigatorDropdownContentProps> = (
  props,
) => {
  const {
    recordings,
    callOutline,
    isLoading,
    isError,
    callPosition,
    onCallPositionRequest,
    currentRecordingId,
    onClose,
  } = props;
  const { audioPosition } = useCallAudioPosition(recordings);
  const { selectCall, selectSegment } = useCallNavigationActions({
    recordings,
    onCallPositionRequest,
  });

  return (
    <div data-call-scroll-container className="max-h-[50vh] overflow-y-auto">
      <div className="px-2 pb-2">
        <CallNavigator
          recordings={recordings}
          callOutline={callOutline}
          isOutlineLoading={isLoading}
          isOutlineError={isError}
          expandedRecordingId={currentRecordingId}
          expandAll
          audioPosition={audioPosition}
          scrollPosition={callPosition}
          onCallRequest={(recordingId) => {
            selectCall(recordingId);
            onClose();
          }}
          onSegmentRequest={(position) => {
            selectSegment(position);
            onClose();
          }}
        />
      </div>
    </div>
  );
};
